import { useEffect, useMemo } from 'react'
import { AxiosError } from 'axios'

import { useQuery } from '@tanstack/react-query'

import { useDispatch } from '@tabeeb/store'
import { useApi } from '@tabeeb/shared/utils/API/hooks'
import { onAddErrorNotification } from '@tabeeb/modules/notification/actions'
import { ApiErrorResponse } from '@tabeeb/contracts/main-api/swagger-main-api'
import getErrorMessageFromResponse from '@tabeeb/modules/shared/utils/getErrorMessageFromResponse'

import { CertificatesQueryKeys } from '../queries'

export interface UseCertificateLocationsProps {
  enabled?: boolean
}

const useCertificateLocations = ({ enabled }: UseCertificateLocationsProps = { enabled: true }) => {
  const dispatch = useDispatch()
  const { certificateServiceApi: api } = useApi()

  const locationsQuery = useQuery({
    queryKey: [CertificatesQueryKeys.Locations],
    queryFn: api.locations.getAllLocationsAsync,
    refetchOnMount: false,
    enabled,
  })

  const { isError: isLocationsError, error: locationsError } = locationsQuery

  useEffect(() => {
    if (isLocationsError && locationsError) {
      const error = locationsError as AxiosError<ApiErrorResponse>
      const message = getErrorMessageFromResponse(error.response?.data)
      dispatch(onAddErrorNotification({ message }))
    }
  }, [isLocationsError, locationsError, dispatch])

  return useMemo(
    () => ({
      locations: locationsQuery.data ?? [],
      loading: locationsQuery.isLoading || locationsQuery.isFetching,
      onReload: locationsQuery.refetch,
    }),
    [locationsQuery.data, locationsQuery.isFetching, locationsQuery.isLoading, locationsQuery.refetch]
  )
}

export default useCertificateLocations
